import { View, Text } from 'react-native'
import theme from '../../../theme'
import { AdvanceSection } from './AdvancesScreen'
import { Card } from '../doti/CardComponent'
import * as Progress from 'react-native-progress'
import { useMemo } from 'react'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { Image } from 'expo-image'
import { withOpacity } from '@/utils/functions.utils'

const GREEN = '#009E4D'

export const AdvancesList = ({ data = null }) => {
    const componentsMap = useMemo(
        () => ({
            monthly_progress: MonthlyProgress,
            monthly_courses_completed: CompletedCourses,
            qualification: Qualification,
            ranking_position: RankingPosition,
            liderboard: RankingPosition,
            my_career_plan: CareerPlanList,
            monthly_courses: MonthlyCourses,
        }),
        [],
    )

    if (!data) return null
    return (
        <View style={{ flex: 1 }}>
            {Object.entries(componentsMap).map(([key, Component]) => {
                const sectionData = data[key]

                if (!sectionData) return null

                const title = sectionData.title ?? ''

                return (
                    <AdvanceSection key={key} title={title}>
                        <Component data={sectionData} />
                    </AdvanceSection>
                )
            })}
        </View>
    )
}

const MonthlyProgress = ({ data }) => {
    const size = 92
    const raw = data?.value
    let percentage = 0

    if (typeof raw === 'number' && !isNaN(raw) && isFinite(raw)) {
        percentage = raw
    }
    const percentNumber = percentage * 100

    const render_progress = `${percentNumber.toFixed(0)}%`
    const color =
        percentage > 0.6 ? theme.colors.colorPrimary : theme.colors.colorSecondary
    const change = data.last_month_difference ?? null

    return (
        <Card>
            <View
                style={{
                    flex: 1,
                    flexDirection: 'row',
                    width: '100%',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                <View
                    style={{
                        justifyContent: 'center',
                    }}
                >
                    <Progress.Circle
                        size={size}
                        progress={percentage}
                        showsText={false}
                        thickness={6}
                        color={color}
                        unfilledColor={theme.colors.lightGray}
                        borderWidth={0}
                        strokeCap='round'
                    />
                    <Text
                        style={{
                            width: size,
                            alignItems: 'center',
                            position: 'absolute',
                            textAlign: 'center',
                            fontSize: 24,
                            fontWeight: '600',
                        }}
                    >
                        {render_progress}
                    </Text>
                </View>
                <View style={{ flexShrink: 1 }}>
                    {change !== null && (
                        <View
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 4,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: 16,
                                    fontWeight: '700',
                                    color:
                                        change > 0
                                            ? GREEN
                                            : change < 0
                                              ? '#E54335'
                                              : theme.colors.textPrimary,
                                }}
                            >
                                {change > 0 ? `+${change}%` : `${change}%`}
                            </Text>
                            {change > 0 && (
                                <Image
                                    source={require('../../../assets/advances_right.png')}
                                    style={{ width: 16, height: 16 }}
                                />
                            )}
                            {change < 0 && (
                                <Image
                                    source={require('../../../assets/advances_wrong.png')}
                                    style={{ width: 16, height: 16 }}
                                />
                            )}
                        </View>
                    )}
                    {data.description && (
                        <Text
                            style={{
                                color: theme.colors.textPrimary,
                                fontSize: 15,
                                marginTop: 4,
                            }}
                        >
                            {data.description}
                        </Text>
                    )}
                </View>
            </View>
        </Card>
    )
}

const CompletedCourses = ({ data }) => {
    const size = 92

    const { total = 0, completed = 0, courses = [] } = data

    const percentage = completed / total

    const color =
        percentage > 0.6 ? theme.colors.colorPrimary : theme.colors.colorSecondary

    return (
        <Card>
            <View
                style={{
                    flex: 1,
                    flexDirection: 'row',
                    width: '100%',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                <View
                    style={{
                        justifyContent: 'center',
                    }}
                >
                    <Progress.Circle
                        size={size}
                        progress={percentage}
                        showsText={false}
                        thickness={6}
                        color={color}
                        unfilledColor={theme.colors.lightGray}
                        borderWidth={0}
                        strokeCap='round'
                    />
                    <Text
                        style={{
                            width: size,
                            alignItems: 'center',
                            position: 'absolute',
                            textAlign: 'center',
                            fontSize: 20,
                            fontWeight: '600',
                        }}
                    >
                        {`${completed} de ${total}`}
                    </Text>
                </View>
                <View style={{ flexShrink: 1, gap: 12 }}>
                    {courses.map((item, index) => (
                        <View
                            key={index.toString()}
                            style={{
                                flexDirection: 'row',
                                alignItems: 'center',
                            }}
                        >
                            <Image
                                source={require('../../../assets/graduation_cap.png')}
                                style={{
                                    width: 16,
                                    height: 16,
                                    marginRight: 4, // opcional para separar el ícono del texto
                                }}
                            />

                            <Text
                                style={{
                                    fontSize: 15,
                                    fontWeight: '600',
                                    color: theme.colors.textPrimary,
                                }}
                            >
                                {index + 1 + '°  '}
                                {item.name}
                            </Text>
                        </View>
                    ))}
                </View>
            </View>
        </Card>
    )
}

const Qualification = ({ data }) => {
    const value = data.value ?? 0
    const period = data.period ?? null
    return (
        <Card>
            <View
                style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    marginBottom: 4,
                }}
            >
                <Text
                    style={{
                        color: theme.colors.colorPrimary,
                        fontWeight: '600',
                        fontSize: 36,
                        lineHeight: 36,
                    }}
                >
                    {Math.min(100, Math.round(value * 100))}%
                </Text>
                {period && (
                    <Text
                        style={{
                            color: theme.colors.colorPrimary,
                            fontWeight: '400',
                            fontSize: 15,
                        }}
                    >
                        {period}
                    </Text>
                )}
            </View>
            <Progress.Bar
                progress={value}
                width={null}
                height={6}
                color={theme.colors.colorPrimary}
                unfilledColor={theme.colors.lightGray}
                borderWidth={0}
            />
        </Card>
    )
}

const RankingPosition = ({ data }) => {
    const ranking = data.ranking ?? []

    return (
        <Card style={{ padding: 0 }}>
            {ranking.map((item, index) => {
                let isPodium = [1, 2, 3].includes(item.rank)
                let isFirstItem = index === 0
                let isLastItem = index === ranking.length - 1
                let isCurrentUser = item.current_user === true
                return (
                    <View key={index} style={{ flex: 1 }}>
                        <View
                            style={[
                                {
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    paddingHorizontal: 16,
                                    paddingVertical: 12,
                                    backgroundColor: isPodium ? '#E9EAF6' : 'white',
                                    overflow: 'hidden',
                                },
                                isFirstItem && {
                                    borderTopLeftRadius: 16,
                                    borderTopRightRadius: 16,
                                },
                                isLastItem && {
                                    borderBottomLeftRadius: 16,
                                    borderBottomRightRadius: 16,
                                },
                            ]}
                        >
                            <Text
                                style={[
                                    {
                                        flexShrink: 1,
                                        color: theme.colors.textPrimary,
                                        fontSize: 15,
                                    },
                                    isCurrentUser && {
                                        fontWeight: '600',
                                        color: theme.colors.colorPrimary,
                                    },
                                ]}
                            >
                                {item.full_name}
                            </Text>
                            <View
                                style={{
                                    flexDirection: 'row',
                                    alignContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Image
                                    source={require('../../../assets/advances_trophy.png')}
                                    style={{
                                        width: 16,
                                        height: 16,
                                        marginRight: 4,
                                        opacity: isPodium ? 1 : 0,
                                        tintColor: withOpacity(
                                            theme.colors.colorPrimary,
                                            0.48,
                                        ),
                                    }}
                                />
                                {item.rank && (
                                    <Text
                                        style={{
                                            color: theme.colors.colorPrimary,
                                            fontWeight: '700',
                                            fontSize: 16,
                                        }}
                                    >
                                        {item.rank}°
                                    </Text>
                                )}
                            </View>
                        </View>
                        <View
                            style={{
                                flex: 1,
                                marginHorizontal: 16,
                                height: isLastItem ? 0 : 1,
                                backgroundColor: isPodium ? 'white' : 'lightgray',
                            }}
                        />
                    </View>
                )
            })}
        </Card>
    )
}

const CareerPlanList = ({ data }) => {
    const courses = data.courses ?? []
    return (
        <Card style={{ gap: 16 }}>
            {courses.map((item, index) => {
                return (
                    <View
                        key={index}
                        style={{
                            flex: 1,
                            alignItems: 'center',
                            flexDirection: 'row',
                        }}
                    >
                        <Image
                            source={require('../../../assets/graduation_cap.png')}
                            style={{
                                width: 16,
                                height: 16,
                                marginRight: 4, // opcional para separar el ícono del texto
                            }}
                        />
                        <Text
                            style={{
                                color: theme.colors.textPrimary,
                                fontSize: 15,
                                fontWeight: '600',
                            }}
                        >
                            {item.name}
                        </Text>
                    </View>
                )
            })}
        </Card>
    )
}

export const MonthlyCourses = ({ data }) => {
    const courses = data.courses ?? []
    return (
        <Card>
            {courses.map((item, index) => {
                const percentage = Math.round((item.progress ?? 0) * 100)
                return (
                    <View key={index} style={{ marginBottom: 24, flex: 1 }}>
                        <View
                            style={{
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                marginBottom: 6,
                            }}
                        >
                            <Text
                                style={{
                                    fontSize: generalFontSize,
                                    lineHeight: generalLineHeigth,
                                    color: '#1A1A1A',
                                    maxWidth: '60%',
                                }}
                                ellipsizeMode='tail'
                                numberOfLines={1}
                            >
                                {item.name}
                            </Text>

                            <View
                                style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                    gap: 0,
                                }}
                            >
                                {item.progress && item.progress < 0.4 && (
                                    <MaterialCommunityIcons
                                        name='alert'
                                        size={18}
                                        color='#E54335'
                                    />
                                )}
                                <Text
                                    style={{
                                        fontSize: generalFontSize,
                                        lineHeight: generalLineHeigth,
                                        fontWeight: '600',
                                        color:
                                            percentage < 40
                                                ? '#E54335'
                                                : theme.colors.colorPrimary,
                                    }}
                                >
                                    {percentage}%
                                </Text>
                            </View>
                        </View>

                        {/* Barra de progreso */}
                        <View
                            style={{
                                height: 8,
                                backgroundColor: '#D8D6E5',
                                borderRadius: 8,
                                flex: 1,
                            }}
                        >
                            <View
                                style={[
                                    {
                                        height: 8,
                                        backgroundColor:
                                            percentage < 40
                                                ? '#E54335'
                                                : theme.colors.colorPrimary,
                                        borderRadius: 8,
                                    },
                                    { width: `${percentage}%` },
                                ]}
                            />
                        </View>
                    </View>
                )
            })}
        </Card>
    )
}

export const generalFontSize = 15
export const generalLineHeigth = 22
